import { formatTime, parseTime } from '@seen/core/dates'
import { parseTimeInput, timeToMinutes } from '#src/utils/timeParser.ts'

export interface TimeOption {
  /** Time in HH:mm format (24-hour) */
  value: string
  /** Formatted display label (e.g., "3:15 PM") */
  label: string
  /** Whether this is a custom time (not on the standard increment) */
  isCustom?: boolean
}

/*
 * Generate base time options for a given range and step interval.
 */
export function generateTimeOptions(
  minTime: string,
  maxTime: string,
  stepMinutes: number
): TimeOption[] {
  const options: TimeOption[] = []
  const [minHour, minMinute] = minTime.split(':').map(Number)
  const [maxHour, maxMinute] = maxTime.split(':').map(Number)

  for (let hour = minHour; hour <= maxHour; hour++) {
    for (let minute = 0; minute < 60; minute += stepMinutes) {
      if (
        (hour === minHour && minute < minMinute) ||
        (hour === maxHour && minute > maxMinute)
      ) {
        continue
      }

      const value = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      const date = parseTime(value)
      const label = formatTime(date)
      options.push({ value, label })
    }
  }
  return options
}

/*
 * Parse search input to a valid time string, or null if invalid.
 */
export function parseSearchToTime(search: string): string | null {
  if (!search) {
    return null
  }
  return parseTimeInput(search)
}

/*
 * Check if a time is within the specified range.
 * Returns false if minTime is null (no valid range).
 */
export function isTimeInRange(
  time: string,
  minTime: string | null,
  maxTime: string
): boolean {
  if (minTime === null) {
    return false
  }
  const timeMinutes = timeToMinutes(time)
  const minMinutes = timeToMinutes(minTime)
  const maxMinutes = timeToMinutes(maxTime)
  return timeMinutes >= minMinutes && timeMinutes <= maxMinutes
}

/*
 * Determine if the search input looks like an invalid time attempt.
 * Returns true if the input contains digits or am/pm but couldn't be parsed.
 */
export function isInvalidTimeInput(
  search: string,
  parsedTime: string | null
): boolean {
  if (!search || parsedTime) {
    return false
  }
  return /\d|am|pm/i.test(search)
}

/*
 * Check if a parsed time already exists in the base options.
 */
export function isTimeInOptionsList(
  time: string | null,
  options: TimeOption[]
): boolean {
  if (!time) {
    return false
  }
  return options.some((opt) => opt.value === time)
}

/*
 * Determine the custom time option to insert, if any.
 *
 * A custom time is shown when:
 * 1. User is typing a valid time that's not in the base options, OR
 * 2. The current value prop is a custom time (not on the increment grid)
 */
export function getCustomTimeToInsert(params: {
  search: string
  parsedTime: string | null
  value: string | undefined
  baseOptions: TimeOption[]
  minTime: string | null
  maxTime: string
}): TimeOption | null {
  const { search, parsedTime, value, baseOptions, minTime, maxTime } = params

  if (search) {
    if (
      !parsedTime ||
      !isTimeInRange(parsedTime, minTime, maxTime) ||
      isTimeInOptionsList(parsedTime, baseOptions)
    ) {
      return null
    }
    return {
      value: parsedTime,
      label: formatTime(parseTime(parsedTime)),
      isCustom: true,
    }
  }

  if (
    value &&
    !isTimeInOptionsList(value, baseOptions) &&
    isTimeInRange(value, minTime, maxTime)
  ) {
    return {
      value: value,
      label: formatTime(parseTime(value)),
      isCustom: true,
    }
  }

  return null
}

/*
 * Insert a custom time option at the correct chronological position.
 */
export function insertCustomTimeOption(
  baseOptions: TimeOption[],
  customOption: TimeOption | null
): TimeOption[] {
  if (!customOption) {
    return baseOptions
  }

  const customMinutes = timeToMinutes(customOption.value)
  const insertIndex = baseOptions.findIndex(
    (opt) => timeToMinutes(opt.value) > customMinutes
  )

  if (insertIndex === -1) {
    return [...baseOptions, customOption]
  }

  return [
    ...baseOptions.slice(0, insertIndex),
    customOption,
    ...baseOptions.slice(insertIndex),
  ]
}

/*
 * Find the option value to scroll to and highlight.
 *
 * Priority:
 * 1. If user typed a parseable time, scroll to it (or closest option)
 * 2. If there's a value prop, scroll to it (or closest option)
 * 3. Otherwise, no scroll target
 */
export function getScrollTargetValue(params: {
  parsedTime: string | null
  value: string | undefined
  displayOptions: TimeOption[]
  minTime: string | null
  maxTime: string
}): string | null {
  const { parsedTime, value, displayOptions, minTime, maxTime } = params

  if (parsedTime) {
    if (displayOptions.some((opt) => opt.value === parsedTime)) {
      return parsedTime
    }
    return findClosestOption(parsedTime, displayOptions)
  }

  if (value && isTimeInRange(value, minTime, maxTime)) {
    return findClosestOption(value, displayOptions)
  }

  return null
}

/*
 * Find the option closest to the target time.
 */
export function findClosestOption(
  targetTime: string,
  options: TimeOption[]
): string | null {
  const targetMinutes = timeToMinutes(targetTime)
  let closest: TimeOption | null = null
  let closestDiff = Infinity

  for (const option of options) {
    const optionMinutes = timeToMinutes(option.value)
    const diff = Math.abs(optionMinutes - targetMinutes)
    if (diff < closestDiff) {
      closestDiff = diff
      closest = option
    }
  }

  return closest?.value ?? null
}

/*
 * Calculate the next highlighted value after arrow key navigation.
 */
export function getNextHighlightedValue(
  currentValue: string | undefined,
  options: TimeOption[],
  direction: 'up' | 'down'
): string | undefined {
  if (options.length === 0) {
    return undefined
  }

  const currentIndex = currentValue
    ? options.findIndex((opt) => opt.value === currentValue)
    : -1

  if (direction === 'down') {
    const nextIndex =
      currentIndex === -1 ? 0 : Math.min(currentIndex + 1, options.length - 1)
    return options[nextIndex].value
  } else {
    const prevIndex =
      currentIndex === -1 ? options.length - 1 : Math.max(currentIndex - 1, 0)
    return options[prevIndex].value
  }
}
